import React from 'react';
import { LayoutGrid, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  PWA_INSTALL_SIZE_LABEL,
  dismissPwaInstall,
  isPwaInstallDismissed,
  usePwaInstall,
} from '@/lib/pwaInstall';

/**
 * Install prompt rendered as a top-of-screen notification on mobile.
 *
 * Two stages:
 *   1. Offer — title + subtitle + [Установить] [Позже].
 *   2. After "Позже" — short reassurance that Settings keeps the option
 *      around, single [OK] to dismiss.
 *
 * Persists the dismissal in localStorage so the banner doesn't reappear
 * across sessions; also auto-dismisses on the `appinstalled` event so
 * users who later open the site in a normal browser tab don't see the
 * offer again. The Settings → Account → Install card stays as a permanent
 * entry point for re-install.
 */
type Stage = 'offer' | 'reassurance';

function InstallNameLine({ size }: { size: string }) {
  // The PWA name baked into the manifest at build time. Lets the
  // banner advertise "Linza Staging (~2 MB)" on staging vs
  // "Linza Metrics (~2 MB)" on prod without an extra i18n string.
  const appName = (import.meta.env.VITE_PWA_NAME as string | undefined) || 'Linza Metrics';
  return (
    <>{appName} ({size})</>
  );
}

export function InstallPwaBanner() {
  const { t } = useT();
  const { platform, canInstall, isStandalone, install } = usePwaInstall();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [dismissed, setDismissed] = React.useState<boolean>(() => isPwaInstallDismissed());
  const [stage, setStage] = React.useState<Stage>('offer');

  // ?force-pwa-banner=1 — visual debug switch. Renders the banner
  // even when Chrome won't fire beforeinstallprompt (e.g. because the
  // PWA is already installed in this browser, or heuristics haven't
  // marked the site as installable yet). Install button degrades to
  // the menu-fallback hint when we don't have a real deferred prompt.
  const forceShow = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).has('force-pwa-banner');

  // The standalone + dismissed gates still apply even with the force
  // switch — there's no value in showing "install me!" while you're
  // literally running inside the installed app.
  if (isStandalone || dismissed) return null;
  if (!forceShow && (!isMobile || !canInstall)) return null;

  function persistDismiss() {
    dismissPwaInstall();
    setDismissed(true);
  }

  function handleInstall() {
    void install().then(outcome => {
      // Outcome 'accepted' lands in the SW install + the appinstalled
      // event handler will persist dismissal. 'dismissed' (user said
      // no in the native dialog) → still hide the banner; the
      // permanent settings entry is enough.
      if (outcome !== 'unavailable') persistDismiss();
    });
  }

  function handleLater() {
    // Don't persist yet — let the reassurance message show first.
    // The flag flips to dismissed when the user taps OK on stage 2.
    setStage('reassurance');
  }

  return (
    <div
      // Top notification slot — pinned above the header, slides down on
      // mount. z-50 sits above the header (z-30) + sidebar Sheet (z-40
      // when open), but below modal dialogs.
      className="
        fixed inset-x-0 top-0 z-50 mx-3 mt-3
        animate-in slide-in-from-top-2 fade-in duration-200
        rounded-xl border border-border bg-card p-4 shadow-lg
      "
      role="dialog"
      aria-label={t('pwa.install.title')}
    >
      {stage === 'offer' && (
        <>
          <div className="flex items-start gap-3">
            <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-primary text-primary-foreground">
              <LayoutGrid className="size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-foreground">
                {t('pwa.install.title')}
              </h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                <InstallNameLine size={PWA_INSTALL_SIZE_LABEL} />
              </p>
              {platform === 'ios' && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {t('pwa.install.iosHowTo')}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleLater}
              aria-label={t('common.dismiss')}
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          </div>
          <div className="mt-3 flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={handleLater}>
              {t('pwa.install.later')}
            </Button>
            {platform !== 'ios' && (
              <Button size="sm" onClick={handleInstall}>
                {t('pwa.install.cta')}
              </Button>
            )}
          </div>
        </>
      )}

      {stage === 'reassurance' && (
        <>
          <p className="text-sm text-foreground">{t('pwa.install.laterHint')}</p>
          <div className="mt-3 flex items-center justify-end">
            <Button size="sm" onClick={persistDismiss}>
              {t('common.ok')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
